import { createFileRoute } from "@tanstack/react-router";
import z from "zod";

import { NavigableListItem } from "@/components";
import { MESSAGE_CENTER_FAQ_ITEM_ID, RETURNS_EMAIL, SUPPORT_EMAIL } from "@/constants";
import { env } from "@/env";
import { Accordion, MarkdownRenderer } from "@/ui";

export const faqs = [
  {
    id: 1,
    title: "How much does an EO care plan cost?",
    body: "The EO Care Plan is completely free. You’ll get a personalized, clinician-guided plan to help you use cannabis safely and effectively—at no cost to you. You only pay for the products you choose to purchase, and all products ordered through your care plan include a discount.",
  },
  {
    id: 2,
    title: "Does insurance help cover the cost?",
    body: "No, EO products and care plans are not currently covered by insurance. We do our best to keep prices affordable and transparent, and the EO care plan is free and includes a discount on products ordered through it.",
  },
  {
    id: 3,
    title: "What's behind the recommendations in a care plan?",
    body: `EO’s guidance and care platform is designed to enable clinically responsible and personalized collaboration between patients, EO’s care team and patient support team.
    <br />Every recommendation in your care plan is based on relevant aspects of your medical history, cannabis use history, daily schedule and the feedback you provide as you go.
    <br />Each recommendation is also informed by rules that have been developed based on our clinical team’s 10,000+ cannabis care patient interactions. These rules are provided to a Bayesian model whose outputs are then provided in plan language via a trained large language model AI. 
    <br />By combining insights from members like you with our clinical team’s expertise in cannabis treatment/research and our state-of-the-art technology platform, we’re well positioned to provide more responsible cannabis use guidance.`,
  },
  {
    id: 4,
    title: "What does it mean if my plan needs clinician review?",
    body: "In some cases, your intake answers indicate that our clinicians need to take a closer look before finalizing your plan. This is often due to specific medications, health conditions, or sensitivities where extra care is needed. It’s part of how we ensure your plan is safe, effective, and right for you.",
  },
  {
    id: 5,
    title: "How does my plan get fine-tuned over time?",
    body: "As you provide feedback about your plan and products, we adjust your plan to better match your experience. That may include changing dose, use or product recommendations. Your plan evolves with your symptoms and real life so it best serves your needs.",
  },
  {
    id: MESSAGE_CENTER_FAQ_ITEM_ID,
    title: "How does the message center work?",
    body: `Our clinician-led team is here to help you on every step of your EO journey. Have questions about how your plan works or want to discuss potential updates? Just let us know. Or maybe you have more general questions about how you can use cannabinoids most safely, responsibly and affordably? Ask away.
    <br />Our team will respond to your message within one business day. And very often, we’ll respond within minutes. 
    <br />If you have any questions that fall outside of cannabinoid-based symptom relief, you should consult your doctor. And if you’re experiencing a medical emergency, please call 911.
    <br />Lastly, the message center also provides a complete history of the key interactions we have with you. So you’ll notice references to key moments, like when you’ve ordered products, completed a check-in or we’ve updated your plan. Just so you have everything in one place.`,
  },
  {
    id: 7,
    title: "What are your phone support hours?",
    body: "Our team is here to answer your calls 9am-5pm EST, Monday through Friday.",
  },
  {
    id: 8,
    title: "Is my health data secure?",
    body: "Yes. EO follows all HIPAA regulations. We never sell your data, and only authorized clinicians and care team members can access your records.",
  },
  {
    id: 9,
    title: "What if I don't want to feel intoxicated?",
    body: "That’s a common goal. Many of our members want relief without feeling “high.” Our products are designed to support symptoms with minimal or no intoxication. Our clinicians take this into account and often recommend low doses, daytime-friendly products or THC-free options to help you feel better without unwanted effects. ",
  },
  {
    id: 10,
    title: "What if I need a cannabis product that’s more potent than the ones that EO provides?",
    body: "If your symptoms require a stronger product than what EO offers (and if recreational or medical cannabis is legal in your state) your clinician may recommend a dispensary-based option. You’ll need to purchase those products outside of EO and those products will not appear in your care plan.",
  },
  {
    id: 11,
    title: "How long should it take to get my cannabis regimen “right”?",
    body: "It varies by person, but many members find meaningful relief within the first 1-2 weeks. The goal is to start low, go slow and listen to your body. Our team will help you refine your regimen based on what’s working, and what’s not.",
  },
  {
    id: 12,
    title: "How do you decide which products, doses and times of use are most right for me?",
    body: "We combine your intake responses with clinical evidence, insights from EO members and the expertise/experience of our clinical team to guide every recommendation. Your plan factors in your goals, daily routine, tolerance and medical history to suggest a regimen that’s both safe and effective.",
  },
  {
    id: 13,
    title: "What if I don’t want to use one of the recommended products?",
    body: `No problem. Your care plan is a starting point, designed to evolve with your preferences, experience, and feedback. If a product doesn’t feel right for you, our team can help you find an alternative that better fits your needs and goals, while keeping your care safe and effective. Just <a href='/messages'>message us through the app</a> or email <a href='mailto:${SUPPORT_EMAIL}'>${SUPPORT_EMAIL}</a> and we’ll work with you to adjust your plan.`,
  },
  {
    id: 14,
    title: "Can I get a plan without THC?",
    body: "Yes. Many EO members choose to avoid THC altogether. If that’s your preference, we’ll recommend CBD-dominant or THC-free products designed to support your goals without intoxicating effects.",
  },
  {
    id: 15,
    title: "How do I get my products?",
    body: "Once your care plan is ready, you can order products directly from your personalized plan. Everything is shipped discreetly and delivered straight to your door. No dispensary visit needed.",
  },
  {
    id: 16,
    title: "What does it mean that products are 'purpose designed & precision dosed'?",
    body: "Every EO product is formulated by clinicians to target specific symptoms, with carefully measured doses to support safe, consistent use. Our products are designed to deliver the right amount of active ingredients to help you feel better, without overdoing it.",
  },
  {
    id: 17,
    title: "What does it mean that all products come with clinician-led guidance?",
    body: `Even if you don't have a care plan, when you buy any EO product you can reach out to our clinical support team at no extra cost. This includes help with questions about dosing, potential medical interactions and responsible/optimal product use. Just call **${env.VITE_EO_CONTACT_NUMBER}** or email <a href='mailto:${SUPPORT_EMAIL}'>${SUPPORT_EMAIL}</a> to get in touch.`,
  },
  {
    id: 18,
    title: "What is hemp-derived Delta-9 THC?",
    body: "Delta-9 THC (or D9 THC) is the most common form of THC found in traditional cannabis products. It’s the compound known for its psychoactive effects and for helping with pain, sleep, and appetite. The difference between marijuana-derived and hemp-derived D9 THC is how it’s sourced. EO’s D9 THC is organic (no synthetic cannabinoids, ever) and it’s extracted from federally legal hemp plants and formulated into precise doses that meet federal guidelines (no more than 0.3% THC by dry weight).<br /><br />This allows us to offer safe, effective, federally legal THC products without the need for a dispensary or medical card. And ship those products to most U.S. states.",
  },
  {
    id: 19,
    title: "How and where is the hemp sourced?",
    body: "EO exclusively sources organic hemp extracts from independent American farms with generations of experience, spanning over 400 acres across North Carolina, Virginia, and Southern California. All hemp is grown without the use of pesticides or herbicides, then carefully extracted and tested before being used in EO products. We partner only with growers who share our commitment to quality, consistency, and safety.",
  },
  {
    id: 20,
    title: "How and where are your products produced?",
    body: "All EO products are made in the U.S. in state-of-the-art facilities located in Pennsylvania, North Carolina and Florida. These facilities are FDA-registered and certified to ISO and GMP standards to ensure strict quality and safety. Before launching any new or updated product, we conduct rigorous efficacy and safety testing through independent research partners like Citrus Labs, so you can trust that what you’re using is both safe and effective.",
  },
  {
    id: 21,
    title: "What does it mean that your products are federally legal?",
    body: "All EO products are made from hemp-derived cannabinoids and contain less than 0.3% THC by dry weight, which makes them federally legal under the 2018 Farm Bill. No dispensary or medical card required.",
  },
  {
    id: 22,
    title: "Why are EO products not FDA approved?",
    body: "Cannabis products, including those made from federally legal hemp, are not currently approved by the FDA for most medical uses. That’s true across the industry, not just at EO.<br /><br />However, all EO products are developed with clinical oversight, produced in FDA-registered facilities certified to ISO and GMP standards, and undergo rigorous safety and quality testing. We follow strict standards to help ensure you’re getting safe, consistent and effective products you can trust.",
  },
  {
    id: 23,
    title: "What is your shipping policy?",
    body: "We ship to all U.S. states except Idaho. We also ship to the District of Columbia, Puerto Rico and the U.S. Virgin Islands. Orders placed after 12pm ET will be shipped the following business day. Economy shipping (5-8 days) is always free.",
  },
  {
    id: 24,
    title: "What is your return policy?",
    body: `Not satisfied with a product? Call **${env.VITE_EO_CONTACT_NUMBER}** to speak with a member of our clinician-led team. We may be able to help you adjust how you’re using it to get better results.<br/><br/>Still not satisfied after 30 days? No problem. Just email **<a href='mailto:${RETURNS_EMAIL}'>${RETURNS_EMAIL}</a>**, and we’ll issue a full refund (excluding shipping) or provide store credit—whichever you prefer.`,
  },
  {
    id: 25,
    title: "What does EO stand for?",
    body: "EO stands for **Expert Care, Optimal Products.** It reflects our commitment to combining clinical expertise with purpose-designed, precision-dosed cannabis products to help you manage symptoms and find relief, safely and effectively.",
  },
  {
    id: 26,
    title: "Why was EO started?",
    body: "For too long, people seeking relief through cannabis-based medicines were left to figure it out on their own—without accessible, affordable clinical guidance, without trusted products and without the ongoing support they deserve.<br/><br/>Often, that meant wasted time, wasted money, confusion and unnecessary risk. So we set out to change the experience by offering purpose-designed products and free clinician-led support to help people use cannabis more safely, confidently and effectively.",
  },
  {
    id: 27,
    title: "Who leads your clinical team?",
    body: "Our team is led by Dr. Brooke Worster, Director of Supportive Oncology at Jefferson Health; Dr. Ben Caplan, a leading cannabis clinician and educator; and Dr. Emily Hajjar, a geriatric pharmacy specialist and professor. Together, they guide our clinical approach, product formulations, and personalized care plans.<br/><br/>Our Clinical Advisory Board includes the above clinicians and Dr. Jacob Strand, Associate Chair, Division of Community Internal Medicine, Geriatrics and Palliative Care at the Mayo Clinic and Dr. Regina Benjamin, a primary physician and former Surgeon General of the United States.",
  },
];

const FAQs = () => {
  const { id: faqId } = Route.useSearch();

  const subtitle = "Here is a handy guide for most frequently asked questions.";

  return (
    <>
      {/* Desktop */}
      <div className="hidden min-h-0 flex-1 flex-row gap-8 overflow-hidden text-content sm:flex">
        <aside className="flex w-60 shrink-0 border-r border-line pr-8 lg:w-72">
          <h2 className="font-semibold">{subtitle}</h2>
        </aside>

        <Accordion.Root
          className="flex flex-1 flex-col overflow-auto py-1.5 pr-2 pl-1.5"
          defaultValue={faqId}
          type="single"
          collapsible
        >
          {faqs.map((faq) => {
            return (
              <Accordion.Item key={faq.id} value={faq.id.toString()}>
                <Accordion.Header>
                  <Accordion.Trigger>
                    {faq.title}
                    <Accordion.Icon />
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content>
                  <MarkdownRenderer>{faq.body}</MarkdownRenderer>
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
        </Accordion.Root>
      </div>

      {/* Mobile */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 text-content sm:hidden">
        <h2 className="text-xl font-semibold">FAQs</h2>

        <p>{subtitle}</p>

        <div className="mt-2 min-h-0 flex-1 overflow-auto pr-1">
          {faqs.map((faq) => {
            return (
              <NavigableListItem
                key={faq.id}
                params={{ id: `${faq.id}` }}
                title={faq.title}
                to="/faqs/$id"
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

export const Route = createFileRoute("/_private/(faqs)/faqs/")({
  component: FAQs,
  validateSearch: z.object({ id: z.string().optional() }),
});
